import React, { useState, useCallback, useRef, useMemo, memo } from 'react';
import { clsx } from 'clsx';
import { Flex, IconButton, Icon, Tooltip, Tabs, ColorSwatch, ColorPicker, Button, Input, icon } from '@nula/core-ui';
import type { ToolPanelProps } from '@nula/core-ui';
import type { ActiveTool, ToolKind } from './use-canvas-tool';
import { EmojiPicker, EmojiPreviewGrid } from '@/components/emoji-picker/emoji-picker';
import type { EmojiRef } from '@/components/emoji-renderer/types';
import { GifPicker } from '@/components/gif-picker/gif-picker';
import { GifPreviewSection } from '@/components/gif-preview-section/gif-preview-section';
import type { GifResult } from '@/components/gif-picker/types';
import { FrameSubPanel, FRAME_PRESETS } from './frame-sub-panel';
import { SHAPE_COMPONENTS, type ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';
import {
  TOOLBAR_SWATCHES,
  FREEDRAW_SIZES,
  DEFAULT_FREEDRAW_COLOR,
} from '@/features/canvas/utils/constants';
import styles from './canvas-toolbar.module.css';
import { canvasShortcuts } from '@/features/canvas/utils/canvas-shortcuts';

export interface ToolPanelConfig {
  toolKind: ToolKind;
  triggerRef: React.RefObject<HTMLElement | null>;
  /** When provided, panel top aligns to this element's top (toolbar container) */
  verticalAnchorRef?: React.RefObject<HTMLElement | null>;
  content: React.ReactNode;
  title: string;
  hideHeader?: boolean;
  search?: ToolPanelProps['search'];
}

interface CanvasToolbarProps {
  activeTool: ActiveTool;
  onSelectTool: (tool: ActiveTool) => void;
  /** When provided, toolbar buttons can open the floating side panel.
   *  No existing button calls this today — wired for future use. */
  onOpenToolPanel?: (config: ToolPanelConfig) => void;
}

const SHAPE_ITEMS: { label: string; shapeKind: ShapeKind }[] = [
  { label: 'Rectangle',    shapeKind: 'rectangle' },
  { label: 'Pill',         shapeKind: 'pill' },
  { label: 'Ellipse',      shapeKind: 'ellipse' },
  { label: 'Diamond',      shapeKind: 'diamond' },
  { label: 'Triangle',     shapeKind: 'triangle' },
  { label: 'Star',         shapeKind: 'star' },
  { label: 'Pentagon',     shapeKind: 'pentagon' },
  { label: 'Hexagon',      shapeKind: 'hexagon' },
  { label: 'Parallelogram',shapeKind: 'parallelogram' },
  { label: 'Chevron',      shapeKind: 'chevron' },
  { label: 'Cross',        shapeKind: 'cross' },
  { label: 'Cylinder',     shapeKind: 'cylinder' },
  { label: 'Arrow',        shapeKind: 'arrow-shape' },
];

interface ToolButtonConfig {
  kind: ToolKind;
  label: string;
  icon: string;
  shortcut: () => string;
}

const TOOLS: ToolButtonConfig[] = [
  { kind: 'freedraw', label: 'Draw',   icon: 'draw',           shortcut: canvasShortcuts.toolDraw },
  { kind: 'sticky',   label: 'Sticky', icon: 'note_stack',     shortcut: canvasShortcuts.toolSticky },
  { kind: 'shape',    label: 'Shape',  icon: 'category',       shortcut: canvasShortcuts.toolShape },
  { kind: 'text',     label: 'Text',   icon: 'text_fields',    shortcut: canvasShortcuts.toolText },
  { kind: 'emoji',    label: 'Emoji, Stickers, GIFs',  icon: 'emoji_emotions', shortcut: canvasShortcuts.toolEmoji },
  { kind: 'frame',    label: 'Frame',  icon: 'web_asset',      shortcut: canvasShortcuts.toolFrame },
];

interface PointerToolConfig {
  kind: ToolKind;
  tool: ActiveTool;
  label: string;
  icon: string;
  shortcut: () => string;
}

const POINTER_TOOLS: PointerToolConfig[] = [
  { kind: 'select', tool: { kind: 'select' }, label: 'Select', icon: 'near_me',       shortcut: canvasShortcuts.toolSelect },
  { kind: 'hand',   tool: { kind: 'hand' },   label: 'Hand',   icon: 'pan_tool_alt',  shortcut: canvasShortcuts.toolHand },
];

type DrawToolKind = 'freedraw' | 'highlighter' | 'eraser';

const DRAW_TOOLS: { kind: DrawToolKind; label: string; icon: string }[] = [
  { kind: 'freedraw',    label: 'Pen',        icon: 'stylus_pen' },
  { kind: 'highlighter', label: 'Highlighter', icon: 'ink_highlighter' },
  { kind: 'eraser',      label: 'Eraser',      icon: 'ink_eraser' },
];

// Pen sizes in canvas units.
const PEN_SIZES = [12, 16, 26] as const;
// Highlighter sizes: thin/medium/thick as absolute canvas units (visibly wide).
const HIGHLIGHTER_SIZES = [22, 34, 58] as const;

interface FreedrawSubPanelProps {
  activeTool: ActiveTool;
  color: string;
  onSelectTool: (tool: ActiveTool) => void;
  onSelectEraser: () => void;
}

// Memoised: parent CanvasToolbar re-renders on every openPanel toggle and
// every external selection change. The sub-panel only depends on activeTool
// + color + the two callbacks, so React.memo skips its re-render until
// those actually change. Drops one large render on every CanvasToolbar pass.
const FreedrawSubPanel = memo(function FreedrawSubPanel({ activeTool, color, onSelectTool, onSelectEraser }: FreedrawSubPanelProps) {
  const activeDrawKind: DrawToolKind | null =
    activeTool.kind === 'eraser' ? 'eraser' :
    activeTool.kind === 'highlighter' ? 'highlighter' :
    activeTool.kind === 'freedraw' ? 'freedraw' : null;

  const currentWidth = (activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter')
    ? activeTool.strokeWidth : null;

  const sizes = activeDrawKind === 'highlighter' ? HIGHLIGHTER_SIZES : PEN_SIZES;

  const handleToolClick = (kind: DrawToolKind) => {
    if (kind === 'eraser') { onSelectEraser(); return; }
    const nextSizes = kind === 'highlighter' ? HIGHLIGHTER_SIZES : PEN_SIZES;
    // Pick the medium size as the default when switching tool kinds
    onSelectTool({ kind, strokeColor: color, strokeWidth: nextSizes[1] });
  };

  const handleSize = (sizeValue: number) => {
    if (!activeDrawKind || activeDrawKind === 'eraser') return;
    onSelectTool({ kind: activeDrawKind, strokeColor: color, strokeWidth: sizeValue });
  };

  const handleColor = (hex: string) => {
    if (!activeDrawKind || activeDrawKind === 'eraser') return;
    onSelectTool({ kind: activeDrawKind, strokeColor: hex, strokeWidth: currentWidth ?? sizes[1] });
  };

  return (
    <Flex vertical className={clsx(styles.subPanel, styles.subPanelBox)} role="group" aria-label="Draw options">
      <Flex vertical gap="xs">
        <span className={styles.sectionLabel}>Tool</span>
        <Flex align="center" gap="xs">
          {DRAW_TOOLS.map(({ kind, label, icon: iconName }) => (
            <Tooltip key={kind} title={label} placement="right">
              <IconButton
                transparent
                aria-label={label}
                aria-pressed={activeDrawKind === kind}
                className={clsx(styles.subToolBtn, activeDrawKind === kind && styles.active)}
                onClick={() => handleToolClick(kind)}
              >
                <Icon name={iconName} size={icon.md} fill={activeDrawKind === kind} />
              </IconButton>
            </Tooltip>
          ))}
        </Flex>
      </Flex>

      <Flex vertical gap="xs">
        <span className={styles.sectionLabel}>Size</span>
        <Flex align="center" gap="xs">
          {FREEDRAW_SIZES.map(({ label }, i) => {
            const sizeValue = sizes[i];
            const isActive = currentWidth === sizeValue;
            const waveStroke = i === 0 ? 1.5 : i === 1 ? 2.5 : 4.5;
            return (
              <Tooltip key={label} title={label} placement="right">
                <IconButton
                  transparent
                  aria-label={label}
                  aria-pressed={isActive}
                  className={clsx(styles.freedrawSizeBtn, isActive && styles.freedrawSizeActive)}
                  onClick={() => handleSize(sizeValue)}
                >
                  <svg
                    className={styles.freedrawSizeWave}
                    width="18"
                    height="8"
                    viewBox="0 0 18 8"
                    fill="none"
                  >
                    <path
                      d="M1 5 C3 1, 6 1, 9 4 C12 7, 15 7, 17 4"
                      stroke="currentColor"
                      strokeWidth={waveStroke}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            );
          })}
        </Flex>
      </Flex>

      <Flex vertical gap="xs">
        <span className={styles.sectionLabel}>Color</span>
        <div className={styles.swatchGrid}>
        {TOOLBAR_SWATCHES.map(({ name, hex }) => (
          <Tooltip key={hex} title={name} placement="right">
            <ColorSwatch color={hex} active={color === hex} onClick={() => handleColor(hex)} />
          </Tooltip>
        ))}
        <Tooltip title="Custom color" placement="right">
          <div className={styles.swatchCellCustom} onMouseDown={(e) => e.stopPropagation()}>
            <ColorPicker value={color} showText={false} size="sm" presets={[]} onChange={(hex) => handleColor(hex)} />
          </div>
        </Tooltip>
        </div>
      </Flex>
    </Flex>
  );
});

type EmojiTab = 'all' | 'emojis' | 'stickers' | 'gifs';

/** "All" tab overview — category sections with "Show all" links, similar to Miro. */
function EmojiAllTab({
  onEmojiSelect,
  onGifSelect,
  onShowAll,
}: {
  onEmojiSelect: (ref: EmojiRef) => void;
  onGifSelect: (result: GifResult) => void;
  onShowAll: (tab: EmojiTab) => void;
}) {
  return (
    <Flex vertical gap="md" className={styles.allTab}>
      {/* Emojis section */}
      <Flex vertical gap="xs">
        <Flex justify="space-between" align="center">
          <span className={styles.sectionLabel}>Emojis</span>
          <Button variant="text" size="sm" onClick={() => onShowAll('emojis')}>
            Show all
          </Button>
        </Flex>
        <EmojiPreviewGrid onSelect={onEmojiSelect} variant="stamp" />
      </Flex>

      {/* Stickers section — lightweight preview, cached after first load */}
      <Flex vertical gap="xs">
        <Flex justify="space-between" align="center">
          <span className={styles.sectionLabel}>Stickers</span>
          <Button variant="text" size="sm" onClick={() => onShowAll('stickers')}>
            Show all
          </Button>
        </Flex>
        <GifPreviewSection mode="stickers" onSelect={onGifSelect} fetchDelay={600} />
      </Flex>

      {/* GIFs section — lightweight preview, cached after first load */}
      <Flex vertical gap="xs">
        <Flex justify="space-between" align="center">
          <span className={styles.sectionLabel}>GIFs</span>
          <Button variant="text" size="sm" onClick={() => onShowAll('gifs')}>
            Show all
          </Button>
        </Flex>
        <GifPreviewSection mode="gifs" onSelect={onGifSelect} />
      </Flex>
    </Flex>
  );
}

/** Content rendered inside the ToolPanel when the emoji button is active. */
function EmojiPanelContent({
  onEmojiSelect,
  onGifSelect,
}: {
  onEmojiSelect: (ref: EmojiRef) => void;
  onGifSelect: (result: GifResult) => void;
}) {
  const [activeTab, setActiveTab] = useState<EmojiTab>('all');
  const [gifSearch, setGifSearch] = useState('');

  const showSearch = activeTab === 'gifs' || activeTab === 'stickers';

  return (
    <Flex vertical className={styles.emojiPanelContent}>
      <Tabs
        activeKey={activeTab}
        onChange={(k) => { setActiveTab(k as EmojiTab); setGifSearch(''); }}
        type="card"
        size="sm"
        items={[
          { key: 'all', label: 'All' },
          { key: 'emojis', label: 'Emojis' },
          { key: 'stickers', label: 'Stickers' },
          { key: 'gifs', label: 'GIFs' },
        ]}
      />

      {/* Search bar — shown for GIFs and Stickers tabs */}
      {showSearch && (
        <div className={styles.gifSearch}>
          <Input
            size="sm"
            placeholder={activeTab === 'gifs' ? 'Search GIFs…' : 'Search stickers…'}
            value={gifSearch}
            allowClear
            prefix={<Icon name="search" size={14} />}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGifSearch(e.target.value)}
          />
        </div>
      )}

      {activeTab === 'all' && (
        <EmojiAllTab
          onEmojiSelect={onEmojiSelect}
          onGifSelect={onGifSelect}
          onShowAll={(tab) => setActiveTab(tab)}
        />
      )}
      {activeTab === 'emojis' && (
        <EmojiPicker
          onSelect={onEmojiSelect}
          defaultVariant="stamp"
          showVariantToggle
          fillContainer
        />
      )}
      {activeTab === 'stickers' && (
        <GifPicker mode="stickers" query={gifSearch} onSelect={onGifSelect} fillContainer />
      )}
      {activeTab === 'gifs' && (
        <GifPicker mode="gifs" query={gifSearch} onSelect={onGifSelect} fillContainer />
      )}
    </Flex>
  );
}

export function CanvasToolbar({ activeTool, onSelectTool, onOpenToolPanel }: CanvasToolbarProps) {
  const [openPanel, setOpenPanel] = useState<ToolKind | null>(null);
  // Remember color across panel open/close — doesn't need to trigger re-renders
  const lastColor = useRef(DEFAULT_FREEDRAW_COLOR);
  if (activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter') {
    lastColor.current = activeTool.strokeColor;
  }

  // Ref to the toolbar root — used as verticalAnchorRef so the panel top aligns
  // to the toolbar top rather than the individual button midpoint.
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Ref per tool button so ToolPanel can anchor horizontally to the button's right edge
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  const handleEmojiSelect = useCallback((ref: EmojiRef) => {
    onSelectTool({ kind: 'emoji', ref });
  }, [onSelectTool]);

  const handleGifSelect = useCallback((result: GifResult) => {
    onSelectTool({
      kind: 'gif',
      data: {
        url: result.url,
        stillUrl: result.stillUrl,
        width: result.width,
        height: result.height,
        source: 'klipy',
      },
    });
  }, [onSelectTool]);

  // Stable element — not recreated on every click so Framer Motion layoutId
  // animation persists across panel open/close cycles.
  const emojiPanelContent = useMemo(
    () => <EmojiPanelContent onEmojiSelect={handleEmojiSelect} onGifSelect={handleGifSelect} />,
    [handleEmojiSelect, handleGifSelect],
  );

  const handleToolClick = useCallback((kind: ToolKind) => {
    // Text activates directly — no sub-panel needed
    if (kind === 'text') {
      onSelectTool({ kind: 'text' });
      setOpenPanel(null);
      return;
    }
    // Emoji opens ToolPanel when handler provided, otherwise falls back to nothing
    if (kind === 'emoji') {
      setOpenPanel(null);
      onOpenToolPanel?.({
        toolKind: 'emoji',
        title: 'Emoji, Stickers, GIFs',
        hideHeader: true,
        triggerRef: emojiButtonRef,
        verticalAnchorRef: toolbarRef,
        content: emojiPanelContent,
      });
      return;
    }
    setOpenPanel(prev => {
      if (prev === kind) return null;
      return kind;
    });
    // Auto-commit pen/thin when opening from a non-draw tool so active state is set immediately
    if (kind === 'freedraw' && activeTool.kind !== 'freedraw' && activeTool.kind !== 'highlighter' && activeTool.kind !== 'eraser') {
      onSelectTool({ kind: 'freedraw', strokeColor: lastColor.current, strokeWidth: PEN_SIZES[0] });
    }
  }, [activeTool.kind, onSelectTool, onOpenToolPanel, emojiPanelContent]);

  const isToolActive = (kind: ToolKind) =>
    activeTool.kind === kind ||
    (kind === 'freedraw' && (activeTool.kind === 'eraser' || activeTool.kind === 'highlighter'));

  const selectEraser = useCallback(() => {
    onSelectTool({ kind: 'eraser' });
  }, [onSelectTool]);

  return (
    <Flex ref={toolbarRef} vertical align="center" className={styles.root} role="toolbar" aria-label="Canvas tools">
      {POINTER_TOOLS.map(pt => {
        const isActive = isToolActive(pt.kind);
        const sc = pt.shortcut();
        return (
          <Tooltip key={pt.kind} title={[pt.label, sc].filter(Boolean).join(' · ')} placement="right">
            <IconButton
              transparent
              active={isActive}
              aria-label={pt.label}
              aria-pressed={isActive}
              className={clsx(styles.toolBtn, isActive && styles.active)}
              hoverBackground={isActive ? 'var(--primary-dim)' : undefined}
              onClick={() => { setOpenPanel(null); onSelectTool(pt.tool); }}
            >
              <Icon name={pt.icon} size={icon.md} fill={isActive} />
            </IconButton>
          </Tooltip>
        );
      })}

      <div className={styles.divider} role="separator" />

      {TOOLS.map(config => {
        const isPanelOpen = openPanel === config.kind;
        const isActive = isToolActive(config.kind);

        return (
          <Flex key={config.kind} className={styles.toolGroup}>
            {isPanelOpen && config.kind === 'shape' && (
              <Flex wrap gap="xs" className={styles.subPanel} role="group" aria-label="Shape options">
                <Tooltip title={['Connector', canvasShortcuts.toolConnector()].filter(Boolean).join(' · ')} placement="right">
                  <IconButton
                    transparent
                    aria-label="Connector"
                    className={clsx(styles.subToolBtn, activeTool.kind === 'connector' && styles.active)}
                    onClick={() => { onSelectTool({ kind: 'connector' }); setOpenPanel(null); }}
                  >
                    <Icon name="conversion_path" size={icon.md} fill={activeTool.kind === 'connector'} />
                  </IconButton>
                </Tooltip>
                {SHAPE_ITEMS.map(item => {
                  const ShapeVariant = SHAPE_COMPONENTS[item.shapeKind];
                  // Pill: draw a plain wide pill rect directly (rx = half its
                  // height). The dimension-compensating variant is for the
                  // stretched node; a static icon reads simplest as a direct rect.
                  const isPill = item.shapeKind === 'pill';
                  return (
                    <Tooltip key={item.shapeKind} title={item.label} placement="right">
                      <IconButton
                        transparent
                        aria-label={item.label}
                        className={clsx(styles.subToolBtn, styles.shapeBtn)}
                        onClick={() => { onSelectTool({ kind: 'shape', shapeKind: item.shapeKind }); setOpenPanel(null); }}
                      >
                        {isPill ? (
                          <svg viewBox="0 0 100 100" width={icon.md} height={icon.md} className={styles.shapeIcon}>
                            <rect x="6" y="32" width="88" height="36" rx="18" ry="18" vectorEffect="non-scaling-stroke" />
                          </svg>
                        ) : (
                          <svg viewBox="-8 -8 116 116" width={icon.md} height={icon.md} className={styles.shapeIcon}>
                            <g vectorEffect="non-scaling-stroke">
                              <ShapeVariant cornerRadius={0} strokeDasharray={undefined} />
                            </g>
                          </svg>
                        )}
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Flex>
            )}

            {isPanelOpen && config.kind === 'sticky' && (
              <Flex wrap gap="xs" className={styles.subPanel} role="group" aria-label="Sticky options">
                {TOOLBAR_SWATCHES.map(({ name, hex }) => (
                  <Tooltip key={hex} title={name} placement="right">
                    <ColorSwatch
                      color={hex}
                      aria-label={`Sticky color ${name}`}
                      className={styles.stickyColorSwatch}
                      onClick={() => { onSelectTool({ kind: 'sticky', stickyColor: hex }); setOpenPanel(null); }}
                    />
                  </Tooltip>
                ))}
              </Flex>
            )}

            {isPanelOpen && config.kind === 'freedraw' && (
              <FreedrawSubPanel
                activeTool={activeTool}
                color={lastColor.current}
                onSelectTool={onSelectTool}
                onSelectEraser={selectEraser}
              />
            )}

            {isPanelOpen && config.kind === 'frame' && (
              <Flex className={styles.subPanel}>
                <FrameSubPanel
                  activePresetId={activeTool.kind === 'frame' ? (activeTool.size ? FRAME_PRESETS.find(p => p.size?.width === activeTool.size!.width && p.size?.height === activeTool.size!.height)?.id ?? null : 'custom') : null}
                  onSelect={(preset) => {
                    onSelectTool({ kind: 'frame', size: preset.size });
                    setOpenPanel(null);
                  }}
                />
              </Flex>
            )}

            {config.kind === 'emoji' ? (
              <Tooltip title={[config.label, config.shortcut()].filter(Boolean).join(' · ')} placement="right">
                <IconButton
                  ref={emojiButtonRef}
                  transparent
                  active={isToolActive(config.kind)}
                  aria-label={config.label}
                  aria-pressed={isToolActive(config.kind)}
                  data-testid={`add-${config.kind}-button`}
                  className={clsx(styles.toolBtn, isToolActive(config.kind) && styles.active)}
                  hoverBackground={isToolActive(config.kind) ? 'var(--primary-dim)' : undefined}
                  onClick={() => handleToolClick(config.kind)}
                >
                  <Icon name={config.icon} size={icon.md} fill={isToolActive(config.kind)} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={[config.label, config.shortcut()].filter(Boolean).join(' · ')} placement="right">
                <IconButton
                  transparent
                  active={isActive}
                  aria-label={config.label}
                  aria-pressed={isActive}
                  data-testid={`add-${config.kind}-button`}
                  className={clsx(styles.toolBtn, isActive && styles.active)}
                  hoverBackground={isActive ? 'var(--primary-dim)' : undefined}
                  onClick={() => handleToolClick(config.kind)}
                >
                  <Icon name={config.icon} size={icon.md} fill={isActive} />
                </IconButton>
              </Tooltip>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}
